// ============================================================================
// useModalAccessibility - shared hook for modal accessibility
// ----------------------------------------------------------------------------
// Provides Escape key handling and focus trapping for all modals.
// Usage: useModalAccessibility(containerRef, onClose, isOpen)
// ============================================================================

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

// Focusable elements queried below are HTMLElements that MAY expose `disabled`
// (only form controls do); the optional member mirrors the runtime read.
type FocusableEl = HTMLElement & { disabled?: boolean };

/** Props the hook returns for spreading onto the modal container. */
interface ModalAccessibilityProps {
    role: 'dialog';
    'aria-modal': 'true';
    'aria-labelledby'?: string;
}

// ---------------------------------------------------------------------------
// Keyboard layers (Packet 2 SUBSTEP D, C19 / O-F5).
//
// Every open modal used to register its OWN document keydown listener. Escape
// then reached every open modal's onClose (stopPropagation on a document
// listener does not stop its siblings, so a confirmation opened over the
// Stage 5 editor dismissed itself AND the editor with one key), and every open
// modal's Tab trap ran (the lower trap, scoped to its own DOM, pulled focus out
// of the dialog above it). Ownership by listener order is not a fix: a
// parent's callback identity changes on ordinary renders, which re-registered
// the listener and moved it to the end of the document's list.
//
// One document listener now dispatches to one owner: the layer that was OPENED
// last. Open order, not listener order and not z-index (ConfirmModal puts its
// z-index on the outer wrapper while the hooked ref is the inner card, and Photo
// Markup's confirmation sits at 20100 over its 20000 surface).
//
// One exception is semantic, not visual: a decision dialog (ConfirmModal passes
// { decisionDialog: true }) outranks every ordinary layer, whenever those opened.
// App mounts its ONE global ConfirmModal beside the surfaces that raise it, and a
// surface can be unmounted and mounted again while the question is still open (the
// Stage 5 editor, by browser Back and Forward). The remounted surface registers
// after the question, and open order alone would hand it Escape, Tab and opening
// focus although the question is the dialog on screen above it. Between two
// decision dialogs, or between two ordinary layers, open order still decides. The owner keeps
// the key whether or not its onClose actually closes anything: ClientMatchDialog
// and PropertyMergeReview refuse to close while busy, and the modal below them
// must not close instead. Ownership ends when the owner closes or unmounts.
//
// A layer whose container ref is given but not attached owns nothing, so a hook
// consumer that is "open" but renders no card cannot swallow the key.
//
// An overlay whose Escape is richer than "close" (Photo Markup: cancel a
// polygon, deselect, then its own unsaved-changes question, handled by its own
// window listener) passes { escapeOwnedByCaller: true }. It is an ordinary
// layer for focus (opening focus, the Tab trap, restoring focus on close) and
// for ownership, but the dispatcher leaves its Escape alone, so the event travels
// on to the overlay's own handler; the modals below it stand down either way.
// ---------------------------------------------------------------------------
interface KeyboardLayer {
    /** The card the layer owns. Given but not attached: the layer owns nothing. Absent: always live while registered. */
    container: RefObject<HTMLElement | null> | null | undefined;
    /** True for an overlay that handles its own Escape: the dispatcher still traps its Tab but leaves Escape to travel on. */
    ownsEscape: boolean;
    /** True for a confirmation that must be answered or dismissed before any ordinary layer beneath it, however they were mounted. */
    decision: boolean;
    /** The latest onClose of a hook modal (read at key time, so a new callback never changes the layer's place). */
    close: () => void;
}

const layers: KeyboardLayer[] = [];
let dispatcherInstalled = false;

const layerIsLive = (layer: KeyboardLayer): boolean => {
    if (!layer.container) return true;
    const el = layer.container.current;
    return !!el && el.isConnected !== false;
};

const topLayer = (): KeyboardLayer | null => {
    let best: KeyboardLayer | null = null;
    for (let i = layers.length - 1; i >= 0; i -= 1) {
        const layer = layers[i]!;
        if (!layerIsLive(layer)) continue;
        // Walking from the newest down: the first live layer is the newest, a decision dialog replaces an ordinary one, and a
        // later-found (older) layer only wins by being a decision dialog when the best so far is not.
        if (!best || (layer.decision && !best.decision)) best = layer;
    }
    return best;
};

// The controls a dialog's Tab order is made of (enabled ones only).
const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// A safe place to put focus inside a dialog when the exact control that should get it back no longer exists: its marked default,
// else its first enabled control that really takes focus, else the dialog's own frame.
const focusEntry = (card: HTMLElement): void => {
    const marked = card.hasAttribute('data-qm-default-focus') ? card : card.querySelector<FocusableEl>('[data-qm-default-focus]');
    const candidates: FocusableEl[] = marked ? [marked] : [];
    candidates.push(...Array.from(card.querySelectorAll<FocusableEl>(FOCUSABLE_SELECTOR)));
    for (const el of candidates) {
        el.focus();
        if (document.activeElement === el) return;
    }
    if (card.tabIndex < 0 && !card.hasAttribute('tabindex')) card.setAttribute('tabindex', '-1');
    card.focus();
};

// v5.40.16 (Wave 16 fix A11Y-13 part 2): full focus trap. Pre-fix Tab
// from the last focusable element in the modal moved focus into the
// underlying page; Shift-Tab from the first focusable wrapped back to
// the page. Keyboard-only users lost the modal context. Now the
// trap cycles within the modal's focusable set. (Now run for the top layer only.)
const trapTab = (container: HTMLElement, e: KeyboardEvent): void => {
    // Gather all focusable elements (visible + not disabled).
    const focusables = Array.from(container.querySelectorAll<FocusableEl>(FOCUSABLE_SELECTOR)).filter(el => {
        // Skip elements that aren't visible (display:none / aria-hidden).
        if (el.disabled) return false;
        if (el.getAttribute && el.getAttribute('aria-hidden') === 'true') return false;
        // offsetParent is null for display:none AND fixed-position-with-no-rect; rule out both.
        const rect = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
        if (rect && rect.width === 0 && rect.height === 0) return false;
        return true;
    });
    if (focusables.length === 0) return;
    // `!` is compile-erased: the length guard above guarantees both ends
    // exist, satisfying noUncheckedIndexedAccess with no runtime change.
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement;
    // Focus on the container itself (a dialog that opens with focus on its own frame, and a click on its empty surface that
    // lands there), or fallen to <body> with nothing focused (the control that held it was removed): Tab enters at the first
    // control and Shift+Tab at the last, never at whatever lies outside the dialog.
    if (active === container || active === document.body || !active) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
    }
    if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
            e.preventDefault();
            last.focus();
        }
    } else {
        if (active === last) {
            e.preventDefault();
            first.focus();
        }
    }
};

const onDocumentKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' && e.key !== 'Tab') return;
    const top = topLayer();
    if (!top) return;
    if (e.key === 'Escape') {
        // An overlay that handles its own Escape: leave the event to travel on to its handler.
        if (top.ownsEscape) return;
        e.stopPropagation();
        top.close();
        return;
    }
    const container = top.container?.current;
    if (container) trapTab(container, e);
};

const registerLayer = (layer: KeyboardLayer): void => {
    if (layers.indexOf(layer) === -1) layers.push(layer);
    if (!dispatcherInstalled && typeof document !== 'undefined') {
        document.addEventListener('keydown', onDocumentKeyDown);
        dispatcherInstalled = true;
    }
};

/**
 * A dialog is on screen: any visible aria-modal element. Every dialog of this
 * hook carries aria-modal (the props it returns), and so do overlays that do
 * not use it. The Stage 1 import's covering-email question waits on this, so it
 * never opens under Photo Markup or over a confirm the operator is answering.
 *
 * A workspace sheet (`data-qm-workspace-sheet`, the Stage 1 Job cockpit on a
 * phone) is aria-modal for its focus trap but is where the operator works, and a
 * confirm opens over it (ConfirmModal sits above it). It is not a dialog to wait
 * for: the question waited unseen behind it and the import stalled (v6.0.113).
 */
export function isAnyModalOpen(): boolean {
    if (typeof document === 'undefined') return false;
    return Array.from(document.querySelectorAll('[aria-modal="true"]:not([data-qm-workspace-sheet])')).some(isOnScreen);
}

// Laid out, and neither it nor an ancestor hidden (a closed drawer left in the page is no dialog).
const isOnScreen = (el: Element): boolean => {
    if (el.getClientRects().length === 0) return false;
    for (let node: Element | null = el; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
    }
    return true;
};

const unregisterLayer = (layer: KeyboardLayer): void => {
    const index = layers.indexOf(layer);
    if (index !== -1) layers.splice(index, 1);
    if (layers.length === 0 && dispatcherInstalled && typeof document !== 'undefined') {
        document.removeEventListener('keydown', onDocumentKeyDown);
        dispatcherInstalled = false;
    }
};

/**
 * Hook that adds Escape key handling and basic focus management to modals.
 *
 * @param {React.RefObject} containerRef - ref to the modal container element
 * @param {Function} onClose - callback to close the modal
 * @param {boolean} [isOpen=true] - whether the modal is currently open
 * @param {string}  [headingId]       - id of the heading element that labels
 *                                      the dialog; wired up to aria-labelledby
 *                                      (UI-3 register fix 2026-04-21).
 * @param {object}  [options]
 * @param {boolean} [options.escapeOwnedByCaller] - the caller handles its own Escape (Photo Markup); the hook still
 *                                      provides opening focus, the Tab trap and focus restoration.
 * @param {boolean} [options.decisionDialog] - a confirmation: it outranks ordinary layers for Escape, Tab and opening focus
 *                                      even when a surface beneath it mounts after it.
 */
export function useModalAccessibility(
    containerRef: RefObject<HTMLElement | null> | null | undefined,
    onClose: (() => void) | null | undefined,
    isOpen: boolean = true,
    headingId?: string,
    options?: { escapeOwnedByCaller?: boolean; decisionDialog?: boolean },
): ModalAccessibilityProps {
    // The latest onClose, read when the key arrives. The layer is registered by `isOpen` alone, so a consumer whose onClose
    // identity changes on every render keeps its place in the open order.
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const layerRef = useRef<KeyboardLayer | null>(null);
    if (!layerRef.current) {
        layerRef.current = {
            container: containerRef,
            ownsEscape: options?.escapeOwnedByCaller === true,
            decision: options?.decisionDialog === true,
            close: () => { closeRef.current?.(); },
        };
    }
    layerRef.current.container = containerRef;
    layerRef.current.ownsEscape = options?.escapeOwnedByCaller === true;
    layerRef.current.decision = options?.decisionDialog === true;

    // Effect 1: take a place in the keyboard-layer order for as long as the modal is open. Escape and the Tab trap are
    // handled by ONE document listener (above) for the top layer only.
    useEffect(() => {
        if (!isOpen) return;
        const layer = layerRef.current!;
        registerLayer(layer);
        return () => unregisterLayer(layer);
    }, [isOpen]);

    // Effect 2: focus the first focusable element on open + restore focus on
    // close (a11y best practice). CRITICALLY this is gated on `isOpen` (and
    // the stable `containerRef`) only — NOT on handleKeyDown — so the modal
    // does not re-steal focus every time the parent re-renders and recreates
    // onClose. Previously this lived in the same effect as the keydown
    // listener, which meant any parent setState propagating into a fresh
    // onClose would refocus the first input in the modal mid-edit. That
    // manifested as the Stage 5 inclusion cursor jumping away on every
    // Enter / Backspace-merge / Delete-merge keystroke.
    // v5.40.14 (Wave 14 fix A11Y-13): also captures + restores the
    // pre-open active element on close (so closing returns focus to whatever
    // triggered the modal — required for keyboard-only users not to lose
    // their place).
    useEffect(() => {
        if (!isOpen) return;
        const container = containerRef?.current;
        if (!container) return;
        // A surface that opens (or mounts again) while a decision dialog is already open above it does not take the focus from
        // that dialog, and so has nothing to give back when it closes. (Effect 1 above has already registered this layer.)
        const owner = topLayer();
        if (owner && owner !== layerRef.current) return;
        const previouslyFocused = ((typeof document !== 'undefined' && document.activeElement) || null) as HTMLElement | null;
        // A modal whose first focusable element is not its safe default can
        // mark the element that should receive opening focus with
        // `data-qm-default-focus`. Without that attribute the behaviour is
        // unchanged: first focusable in DOM order. This exists because DOM
        // order is a layout decision, while the safe default is a semantic
        // one, and a money-guard dialog cannot let those two disagree.
        // The container may carry the marker itself (Photo Markup opens with focus on its own frame, so a first Enter does nothing).
        const focusable = container.hasAttribute('data-qm-default-focus')
            ? container
            : (container.querySelector<FocusableEl>('[data-qm-default-focus]')
                || container.querySelector<FocusableEl>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
        if (focusable === container && container.tabIndex < 0 && !container.hasAttribute('tabindex')) {
            container.setAttribute('tabindex', '-1');
        }
        if (focusable) {
            focusable.focus();
        // tabIndex is always a number on a real HTMLElement, so `=== undefined`
        // is never true at runtime; the cast preserves the original defensive
        // check without TS flagging a no-overlap comparison.
        } else if ((container.tabIndex as number | undefined) === undefined || container.tabIndex < 0) {
            container.setAttribute('tabindex', '-1');
            container.focus();
        }
        return () => {
            try {
                // This layer has just unregistered (Effect 1's cleanup runs first). If focus is now inside the layer that owns the
                // keyboard (a decision dialog left open when this surface unmounted), it is not ours to take back.
                const remaining = topLayer();
                const remainingCard = remaining?.container?.current;
                if (remainingCard && remainingCard.contains(document.activeElement)) return;
                if (previouslyFocused && typeof previouslyFocused.focus === 'function'
                    && document.body.contains(previouslyFocused)) {
                    previouslyFocused.focus();
                    if (document.activeElement === previouslyFocused) return;
                }
                // The exact control is gone (the surface that held it was destroyed and mounted again while this dialog stayed
                // open) or cannot take focus. Once every layer closing in this commit has had its say, and ONLY if nothing is
                // focused, hand focus to a safe control of the layer that now owns the keyboard rather than leaving it on
                // <body> behind that layer. (A layer that closes in the same commit may put focus back itself, e.g. a markup
                // overlay and its own question both closing on Discard, and that exact return must win.)
                queueMicrotask(() => {
                    try {
                        const active = document.activeElement;
                        if (active && active !== document.body) return;
                        const card = topLayer()?.container?.current;
                        if (card) focusEntry(card);
                    } catch { /* non-fatal */ }
                });
            } catch { /* non-fatal */ }
        };
    }, [isOpen, containerRef]);

    // v5.40.14 (Wave 14 fix A11Y-13): lock background scroll while the modal
    // is open so wheel/touch gestures don't slide the page behind the dialog.
    // Pre-fix any modal that didn't have its own backdrop captured wheel
    // events let the page underneath continue to scroll.
    useEffect(() => {
        if (!isOpen || typeof document === 'undefined') return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prevOverflow; };
    }, [isOpen]);

    // Returns props to spread on the modal container div
    const props: ModalAccessibilityProps = {
        role: 'dialog',
        'aria-modal': 'true',
    };
    if (headingId) props['aria-labelledby'] = headingId;
    return props;
}
